import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-97874de0.js';
import { c as createMutationObserver } from './mutation-observer-db8757e6.js';
import { h as hasSlottedElements } from './shadow-dom-cc0bc152.js';

const groupCss = ":host{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:flex;flex-direction:column;position:relative;width:19.75rem;min-width:12rem;border-color:var(--theme-group-item--border-color)}:host .group-header{height:4rem;min-height:4rem;max-height:4rem;border-radius:var(--theme-group--border-radius) var(--theme-group--border-radius) 0 0;display:flex;background-color:var(--theme-group-item--background);color:var(--theme-group-header--color);cursor:pointer}:host .group-header:not(.disabled):not(:disabled){cursor:pointer}:host .group-header:not(.disabled):not(:disabled):hover{background-color:var(--theme-group-item--background--hover)}:host .group-header:not(.disabled):not(:disabled):hover.selected{background-color:var(--theme-group-item--background--selected)}:host .group-header:not(.disabled):not(:disabled){cursor:pointer}:host .group-header:not(.disabled):not(:disabled):active,:host .group-header:not(.disabled):not(:disabled).active{background-color:var(--theme-group-item--background--active)}:host .group-header:not(.disabled):not(:disabled):active.selected,:host .group-header:not(.disabled):not(:disabled).active.selected{background-color:var(--theme-group-item--background--selected)}:host .group-header:not(.disabled):not(:disabled):focus-visible{border:1px solid var(--theme-color-focus-bdr);border-radius:var(--theme-default-border-radius) var(--theme-default-border-radius) 0px 0px;outline:none}:host .group-header.selected{background-color:var(--theme-group-item--background--selected)}:host .group-header.selected .group-header-selection-indicator{background-color:var(--theme-group-item-indicator--background--selected)}:host .group-header .group-header-selection-indicator{background-color:var(--theme-color-input--focus)}:host .group-header .group-header-selection-indicator.group-header-selection-indicator-item-selected{background-color:var(--theme-group-item-indicator--background--selected)}:host .group-header-clickable{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:flex;width:100%;min-width:0}:host .group-header-selection-indicator{width:0.25rem;border-top-left-radius:var(--theme-group--border-radius)}:host .group-header-content{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:flex;flex-direction:row;justify-content:space-between;min-width:0;flex-grow:1;flex-basis:0;padding:0.5rem;padding-left:0}:host .group-header-content .group-header-props-container{width:100%}:host .group-header-content .group-header-title{display:flex;align-items:center;font-size:1rem;font-weight:700;height:1.5rem}:host .group-header-content .group-header-title>*{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;padding-right:0}:host .group-header-content .group-subheader{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;height:1.25rem;font-size:0.875rem;color:var(--theme-group-subheader--color)}:host .expand-icon{padding:0.125rem 0.437rem;color:var(--theme-color-std-text)}:host .btn-expand-header{margin:0.5rem;margin-inline-end:0.25rem;min-width:1.5rem}:host .group-content{display:flex;flex-direction:column;gap:1px;margin-top:1px}:host .footer{visibility:collapse;height:auto;min-height:0}:host .footer-visible{visibility:visible}:host .hidden{display:none}";
const IxGroupStyle0 = groupCss;

const Group = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.selectGroup = createEvent(this, "selectGroup", 7);
        this.selectItem = createEvent(this, "selectItem", 7);
        this.collapsedChanged = createEvent(this, "collapsedChanged", 7);
        this.observer = null;
        this.suppressHeaderSelection = false;
        this.header = undefined;
        this.subHeader = undefined;
        this.collapsed = true;
        this.selected = false;
        this.index = undefined;
        this.expandOnHeaderClick = false;
        this.itemSelected = false;
        this.slotSize = this.groupItems.length;
        this.footerVisible = false;
        this.showExpandCollapsedIcon = false;
    }
    selectedChanged(newSelected) {
        if (newSelected === false) {
            this.changeItemIndex();
        }
    }
    get dropdownItems() {
        return Array.from(this.hostElement.querySelectorAll('ix-group-dropdown-item'));
    }
    get groupItems() {
        return Array.from(this.hostElement.querySelectorAll('ix-group-item:not(.footer)'));
    }
    get groupContent() {
        var _a;
        return (_a = this.hostElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.group-content');
    }
    onExpandClick(event) {
        const oldCollapsed = this.collapsed;
        this.collapsed = !this.collapsed;
        const { defaultPrevented } = this.collapsedChanged.emit(this.collapsed);
        event.stopPropagation();
        if (defaultPrevented) {
            this.collapsed = oldCollapsed;
        }
    }
    onHeaderClick(event) {
        if (this.suppressHeaderSelection) {
            this.onExpandClick(event);
            return;
        }
        this.changeHeaderSelection(!this.selected);
        this.changeItemIndex();
    }
    changeHeaderSelection(newSelection) {
        const oldIsHeaderSelected = this.selected;
        const newIsHeaderSelected = newSelection;
        this.selected = newIsHeaderSelected;
        const { defaultPrevented } = this.selectGroup.emit(newIsHeaderSelected);
        if (defaultPrevented) {
            this.selected = oldIsHeaderSelected;
            return;
        }
    }
    changeItemIndex(index) {
        const oldIndex = this.index;
        const newIndex = index === this.index ? undefined : index;
        if (this.index === newIndex) {
            return;
        }
        this.index = newIndex;
        const { defaultPrevented } = this.selectItem.emit(newIndex);
        if (defaultPrevented) {
            this.index = oldIndex;
            return;
        }
        const items = this.groupItems;
        items.forEach((item, i) => {
            item.selected = i === this.index;
        });
        this.itemSelected = items.some((item) => item.selected);
    }
    onSlotChange() {
        var _a;
        const slot = (_a = this.hostElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('slot[name="footer"]');
        if (slot) {
            this.footerVisible = hasSlottedElements(slot);
        }
    }
    componentWillRender() {
        this.groupItems.forEach((item, index) => {
            item.selected = index === this.index;
            item.index = index;
        });
    }
    componentDidLoad() {
        this.observer = createMutationObserver(() => {
            this.slotSize = this.groupItems.length;
        });
        if (!this.groupContent) {
            return;
        }
        this.observer.observe(this.groupContent, {
            childList: true,
        });
    }
    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
    onItemClicked(event) {
        if (event.target instanceof HTMLElement) {
            const item = event.target;
            const index = this.groupItems.indexOf(item);
            this.changeItemIndex(index);
        }
    }
    render() {
        return (h(Host, { key: 'ba5dacbded59627971ce06abe0536f1fb11e1ac6' }, h("div", { key: 'b8271e148a69c0dba1daa12f9fc3474c4dd7725c', class: {
                'group-header': true,
                expand: !this.collapsed,
                selected: this.selected,
            }, tabindex: "0" }, h("div", { key: 'a7f328cbbd30054d7381e8164d73feb73232874a', class: "group-header-clickable", onClick: (e) => this.onHeaderClick(e) }, h("div", { key: 'ebb68beb1559541ba1324c10ca50bb978a8f1f30', class: {
                'group-header-selection-indicator': true,
                'group-header-selection-indicator-item-selected': this.itemSelected,
            } }), h("div", { key: 'd65309e476a3789116a87009238659d0c28f659b', class: "btn-expand-header" }, h("ix-icon", { key: 'bd37fc8491bc0a755755074ea5822c8bc68ef0ac', "data-testid": "expand-collapsed-icon", class: {
                hidden: !this.showExpandCollapsedIcon,
            }, name: this.collapsed ? 'chevron-right-small' : 'chevron-down-small', onClick: (event) => this.onExpandClick(event) })), h("div", { key: '8bbba19917b63556b609479d3dc8a1e1fcf7526c', class: "group-header-content" }, this.header ? (h("div", { class: "group-header-props-container" }, h("div", { class: "group-header-title" }, h("span", { title: this.header }, this.header)), h("div", { class: "group-subheader", title: this.subHeader }, this.subHeader))) : null, h("slot", { key: '62d1ebdd6d3fce5c3847bdf90a71cdf2837010ce', name: "header" }))), h("ix-group-context-menu", { key: '34c18ddda5c2ca757fe89d4060fd6201e09ae15f' }, h("slot", { key: '97bf0c691865419cb13c25ab4c1eaa25dd79ac8b', name: "dropdown" }))), h("div", { key: '7e39df1d6867d596871a776e7c1b05c8d643078d', class: {
                'group-content': true,
            } }, h("div", { key: '98cad46478ad8be987c6dc6e944a7b7f550badb4', style: {
                display: this.collapsed ? 'none' : 'contents',
            } }, h("slot", { key: '247e5fd3ad89462835d4dff5763861def669d6e2', onSlotchange: () => {
                var _a;
                const slot = (_a = this.hostElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('slot:not([name])');
                this.showExpandCollapsedIcon = hasSlottedElements(slot);
            } }), h("ix-group-item", { key: 'e058e81b85f5388522b9255bcc68398677d73e5a', suppressSelection: true, focusable: false, class: {
                footer: true,
                'footer-visible': this.footerVisible,
            } }, h("slot", { key: 'a2c572c20cde75e922bd69f7c467b5f4109c2322', name: "footer", onSlotchange: () => this.onSlotChange() }))))));
    }
    get hostElement() { return getElement(this); }
    static get watchers() { return {
        "selected": ["selectedChanged"]
    }; }
};
Group.style = IxGroupStyle0;

export { Group as ix_group };

//# sourceMappingURL=ix-group.entry.js.map